"use client";

import {
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { ChevronDown, Search, SlidersHorizontal, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "../ui/drawer";
import { useIsMobile } from "@erp/shared-libs/hooks";
import { cn } from "@erp/shared-libs/utils";
import { Separator } from "../ui";

/**
 * 單一已套用條件 chip
 */
export interface SearchFilterChip {
  /**
   * 唯一識別（通常用欄位 key）
   */
  id: string;
  /**
   * chip 顯示文字
   */
  label: ReactNode;
  /**
   * 這是「不包含」的條件：用灰階顯示，和包含的條件（主色）分開
   */
  isExcluded?: boolean;
  /**
   * 移除此條件；不傳則此 chip 不可移除（常駐條件）
   */
  onRemove?: () => void;
}

interface SearchFilterCardProps {
  /**
   * 關鍵字搜尋區（page 傳入 SearchInputForm）；卡片會限制其寬度，不會拉滿整列。
   *
   * 手機上「已篩選狀態」列不顯示時（見 `filterSlot`），搜尋框一送出就直接查詢：
   * 卡片會把這個元件的 `onSubmit` 包一層，先照原本的寫回條件，再呼叫 `onSearch`
   */
  searchSlot: ReactNode;
  /**
   * 篩選鈕區（page 傳入一或多個 OptionMenu）；桌面接在搜尋框右側一顆一顆排，排滿才換到下一行，
   * 查詢 / 清除維持原位；手機收進 bottom sheet。
   *
   * 有傳的頁面，手機不顯示「已篩選狀態」列：查詢、清除與已套用的條件都在 bottom sheet 裡，
   * 「篩選」鈕上的數字是已套用的條件數（含關鍵字）
   */
  filterSlot?: ReactNode;
  /**
   * 已套用條件 chips（由卡片自行渲染 + 進場動畫）
   */
  chips?: SearchFilterChip[];
  /**
   * 已套用篩選數量；顯示在手機「篩選」鈕的 badge 上
   */
  activeFilterCount?: number;
  /**
   * 是否隱藏「查詢」鈕（例如即時查詢的頁面）
   */
  hideSearchButton?: boolean;
  /**
   * 卡片外層 className
   */
  className?: string;
  /**
   * 點「查詢」
   */
  onSearch: () => void;
  /**
   * 點「清除全部」
   */
  onClearAll: () => void;
}

/**
 * 整合篩選卡（ai 外觀 · 搜尋 + 篩選列）
 *
 * 主列：固定寬搜尋框 ｜ 篩選鈕（接在搜尋框後面，排滿才換行）｜（靠右）清除 + 查詢。
 * 篩選鈕和搜尋框在同一個 flex-wrap 裡：這一列還放得下就接著排，放不下的才換到下一行；查詢 / 清除始終固定靠右、不跳位。
 * 已套用 chips 收在主列下方分隔列，由卡片自行渲染（含進場動畫）。
 * 手機版：篩選收進「篩選」鈕點開底部 bottom sheet；已套用的 chips 在 sheet 上方排成一排、可左右滑。
 */
export function SearchFilterCard({
  searchSlot,
  filterSlot,
  chips = [],
  activeFilterCount = 0,
  hideSearchButton,
  className,
  onSearch,
  onClearAll,
}: SearchFilterCardProps) {
  // =================================================
  // Hooks
  // =================================================
  /**
   * 是否為手機版（手機把篩選收進 bottom sheet）
   */
  const isMobile = useIsMobile();

  /**
   * 手機 bottom sheet 是否開啟
   */
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  /**
   * 搜尋框送出了幾次（只在送出後要直接查詢時累加）。
   * 不在送出當下直接呼叫 `onSearch`：那時頁面剛把關鍵字寫進條件、還沒重新渲染，
   * `onSearch` 讀到的還是舊條件；等這個數字變了、畫面更新後再查
   */
  const [searchSubmitCount, setSearchSubmitCount] = useState(0);

  /**
   * 用最新的 `onSearch` 查詢
   */
  const runSearch = useEffectEvent(() => onSearch());

  // =================================================
  // Variables
  // =================================================
  /**
   * 是否有任何已套用 chip（決定 chips 列是否出現）
   */
  const hasActiveFilters = chips.length > 0;

  /**
   * 手機由篩選 bottom sheet 包辦查詢與清除：有篩選 sheet 的頁面，手機不顯示「已篩選狀態」列
   */
  const isSheetOnlyMobile = isMobile && filterSlot != null;

  /**
   * 「已篩選狀態」列要不要顯示
   */
  const isAppliedFilterBarVisible = !isSheetOnlyMobile;

  /**
   * 搜尋框送出後要不要直接查詢：手機沒有「已篩選狀態」列上的「查詢」鈕時才需要
   */
  const isSearchOnSubmit = isSheetOnlyMobile;

  /**
   * 手機「篩選」鈕上的數字。
   * 「已篩選狀態」列不顯示時，已套用的條件（含關鍵字）改在 bottom sheet 裡看，
   * 所以數字是全部已套用的條件數，使用者才知道有條件在生效；其餘情況照頁面給的篩選數
   */
  const filterBadgeCount = isSearchOnSubmit ? chips.length : activeFilterCount;

  // =================================================
  // Functions
  // =================================================
  /**
   * 手機 bottom sheet 內點「查詢」：先觸發查詢再關閉 sheet
   */
  const handleSheetSearch = () => {
    onSearch();
    setIsSheetOpen(false);
  };

  /**
   * 搜尋框送出、畫面更新之後直接查詢
   */
  useEffect(() => {
    if (searchSubmitCount === 0) return;

    runSearch();
  }, [searchSubmitCount]);

  /**
   * 篩選 bottom sheet 上面再疊一層對話框或抽屜時（例：點商品分類開的選擇器），
   * 在那一層上的操作不算「點了 sheet 外面」，sheet 不關。
   *
   * 那一層不是 sheet 的子元件時，sheet 會把它當成外面。觸控裝置是等到 click 才判斷有沒有點在外面，
   * 那時上面那一層剛關掉、sheet 又變回最上層，於是 sheet 跟著被關掉（滑鼠是在 pointerdown 當下判斷，不會發生）
   */
  const handleSheetInteractOutside = (event: Event) => {
    const target = event.target as Element | null;

    if (target?.closest?.('[role="dialog"]') != null) {
      event.preventDefault();
    }
  };

  // =================================================
  // Render
  // =================================================
  /**
   * 渲染已套用 chips（含進場動畫；可移除者帶 x 鈕）
   */
  const renderChips = () =>
    chips.map((chip) => (
      <span
        key={chip.id}
        data-excluded={chip.isExcluded === true}
        className={cn(
          `text-dense animate-in fade-in zoom-in-95 slide-in-from-left-1 flex h-7 shrink-0 items-center gap-1
          rounded-md pl-2.5 pr-1 font-medium duration-200`,
          chip.isExcluded ? "bg-muted text-muted-foreground-darker" : "bg-primary-50 text-primary"
        )}
      >
        <span className="whitespace-nowrap">{chip.label}</span>

        {chip.onRemove ? (
          <button
            type="button"
            aria-label="移除此篩選"
            onClick={chip.onRemove}
            className={cn(
              "grid size-4 place-items-center rounded-full transition-colors",
              chip.isExcluded
                ? "hover:bg-border active:bg-border"
                : "hover:bg-primary-200/60 active:bg-primary-300/60"
            )}
          >
            <X className="size-3" />
          </button>
        ) : (
          <span className="w-1.5" />
        )}
      </span>
    ));

  /**
   * 搜尋框：送出後要直接查詢時，把它的 `onSubmit` 包一層（先照原本的寫回條件，再記一次送出）
   */
  const resolvedSearchSlot =
    isSearchOnSubmit &&
    isValidElement<{ onSubmit?: (...args: unknown[]) => void }>(searchSlot) &&
    typeof searchSlot.props.onSubmit === "function"
      ? cloneElement(searchSlot, {
          onSubmit: (...args: unknown[]) => {
            searchSlot.props.onSubmit?.(...args);
            setSearchSubmitCount((count) => count + 1);
          },
        })
      : searchSlot;

  return (
    <div className={cn("@container mb-4", className)}>
      {/* =======================================================
           上層：搜尋框 + 篩選鈕（flex-wrap，排滿才換行）
           ======================================================= */}
      <div
        className={cn("flex flex-wrap items-center gap-2.5", isAppliedFilterBarVisible && "mb-4")}
      >
        {/* 搜尋框：限制寬度，不拉滿（手機佔滿整列） */}
        <div className="@2xl:max-w-lg flex w-full items-center">{resolvedSearchSlot}</div>

        {/* 桌面：篩選鈕直接當主列的項目（`contents`），接在搜尋框後面一顆一顆排，排滿才換行；
            不另外包成一組，否則一列放不下全部時整組會掉到下一行，搜尋框右邊空著 */}
        {filterSlot ? <div className="hidden sm:contents">{filterSlot}</div> : null}

        {/* 手機：篩選收成一顆「篩選」鈕（整列另起，開 bottom sheet）；亮起樣式同 OptionMenu */}
        {filterSlot ? (
          <Button
            type="button"
            variant="aiFilter"
            active={filterBadgeCount > 0}
            className="text-dense flex h-10 w-full justify-between sm:hidden"
            onClick={() => setIsSheetOpen(true)}
          >
            <span className="flex items-center gap-2">
              <SlidersHorizontal className="size-4" />
              篩選
              {filterBadgeCount > 0 ? (
                <span
                  className="bg-primary text-primary-foreground h-4.5 min-w-4.5 grid place-items-center rounded-full px-1
                    text-[10px] font-bold"
                >
                  {filterBadgeCount}
                </span>
              ) : null}
            </span>
            <ChevronDown className="text-muted-foreground size-3.5" />
          </Button>
        ) : null}
      </div>

      {/* =======================================================
           中層：已套用 chips（有 chip 才出現）；手機橫向滑動
           手機有篩選 bottom sheet 時也不出現（查詢與清除在 sheet 裡）
           ======================================================= */}
      {!isAppliedFilterBarVisible ? null : (
        <div
          className="animate-in bg-on-background border-border-lighter fade-in @lg:flex-row @lg:items-center @lg:pl-4
            @lg:pr-1.5 flex flex-col flex-wrap gap-2.5 overflow-x-auto rounded-xl border p-1.5 duration-200"
        >
          <div className="@lg:px-0 flex flex-1 items-start gap-2.5 px-2">
            <div className="flex items-center gap-1.5 py-1">
              <span className="text-muted-foreground-darker text-dense shrink-0">已篩選狀態</span>
            </div>

            <div className="flex flex-col self-stretch py-0.5">
              <Separator orientation="vertical" />
            </div>

            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">{renderChips()}</div>
          </div>

          {/* =======================================================
             底欄：清除（左 ghost）+ 查詢（右 ai）— 動作放最下方，符合操作流程
             ======================================================= */}
          <div className="border-border-lighter @lg:ml-auto @lg:border-none @lg:pt-0 flex items-center gap-2 border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground-darker text-dense @lg:flex-none flex-1"
              disabled={!hasActiveFilters}
              onClick={onClearAll}
            >
              <Trash2 className="size-3.5!" />
              清除全部
            </Button>

            {hideSearchButton ? null : (
              <Button
                type="button"
                size="sm"
                variant="dark"
                className="text-dense flex-1 lg:flex-none"
                onClick={onSearch}
              >
                查詢
              </Button>
            )}
          </div>
        </div>
      )}

      {/* =======================================================
           手機：篩選 bottom sheet（裝全部篩選 + 查詢/清除）
           ======================================================= */}
      {isMobile && filterSlot ? (
        <Drawer open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <DrawerContent
            className="flex max-h-[80vh] w-full flex-col rounded-t-2xl pb-[env(safe-area-inset-bottom)]"
            onPointerDownOutside={handleSheetInteractOutside}
            onInteractOutside={handleSheetInteractOutside}
          >
            {/* 標題列 */}
            <div className="border-border-lighter flex items-center justify-between border-b px-5 py-4">
              <DrawerTitle className="text-base font-bold">篩選條件</DrawerTitle>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setIsSheetOpen(false)}
              >
                <X className="size-5" />
              </Button>
            </div>

            {/* 已套用的條件：頁面上不顯示「已篩選狀態」列時，在這裡看與逐一移除（移除後按「查詢」套用）。
                固定一排、不換行，放不下就左右滑：條件多的時候不會把下面的篩選鈕擠出畫面 */}
            {isSearchOnSubmit && hasActiveFilters ? (
              <div
                className="border-border-lighter flex shrink-0 flex-nowrap gap-1.5 overflow-x-auto border-b px-5 py-3
                  [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {renderChips()}
              </div>
            ) : null}

            {/* 全部篩選（直向堆疊）
                w-full 只套在下拉／抽屜這類觸發鈕；Radix 的 Checkbox（role=checkbox）與
                PillToggleGroup 的 pill（role=radio）本身就是 button，被撐滿會擠掉同列文字，故排除。
                每個篩選都 shrink-0：直向 flex 的子項預設會被壓縮，螢幕不夠高時篩選鈕會被壓扁；
                不壓縮，放不下就在這一區捲動 */}
            <div
              className="flex min-h-0 flex-col gap-3 overflow-y-auto px-5 py-4 [&>*]:shrink-0
                [&_button:not([role=checkbox]):not([role=radio])]:w-full
                [&_button:not([role=checkbox]):not([role=radio])]:justify-between"
            >
              {filterSlot}
            </div>

            {/* 底部操作 */}
            <div className="border-border-lighter flex gap-3 border-t px-5 py-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  onClearAll();
                  setIsSheetOpen(false);
                }}
              >
                <Trash2 className="size-4" />
                清除
              </Button>
              <Button
                type="button"
                variant="default"
                className="flex-1"
                onClick={handleSheetSearch}
              >
                <Search className="size-4" />
                查詢
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      ) : null}
    </div>
  );
}
